import {
  ACTION_CLICK_ADD_TO_CALENDAR,
  ACTION_START_CANCEL_RESERVATION,
  ACTION_START_CANCEL_TRANSFER_RESERVATION,
  ACTION_START_MODIFY_RESERVATION,
  ACTION_START_TRANSFER_RESERVATION,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { Alert, Typography } from 'omakase';
import { ButtonCoreProps } from 'omakase/Button/ButtonCore';
import { FC, useState } from 'react';
import { Instant } from 'js-joda';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import { experienceToData, pastPurchaseToData } from 'utils/analytics/amplitude';
import { getAppTarget } from 'utils/deviceUtils';
import { hasAnyMoniesInvolved } from 'utils/purchase';
import { isNil, some } from 'es-toolkit/compat';
import { joinPath } from 'utils/pathUtils';
import { parse, stringify } from '@tock/query-string';
import { shouldUsePaidQuickCheckout } from 'utils/quickCheckoutUtils';
import { useActions } from '@tock/shared/utils/connect';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useApp } from 'selectors/appSelector';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useHistory, useRouteMatch } from 'react-router';
import CalendarExportModal from 'components/business/receipts/CalendarExportModal';
import CollapsibleButtonGroup from 'components/buttons/CollapsibleButtonGroup';
import PQCLoginForm from 'components/business/receipts/PQCLoginForm';
import ReceiptCalendarButton from 'components/business/receipts/ReceiptCalendarButton';
import ReceiptCancelButton from 'components/business/receipts/ReceiptCancelButton';
import ReceiptCancelTransferButton from 'components/business/receipts/ReceiptCancelTransferButton';
import ReceiptModifyButton from 'components/business/receipts/ReceiptModifyButton';
import ReceiptTransferButton from 'components/business/receipts/ReceiptTransferButton';
import protos from 'utils/protos';

export interface ReceiptButtonProps extends Omit<
  ButtonCoreProps,
  'variant' | 'size' | 'iconStart' | 'selected'
> {
  variant?: 'button' | 'link';
}

interface ReceiptActionButtonsProps {
  business: consumer.IBusinessConfig;
  purchase: core.PastPurchase;
  fetchData: () => unknown;
  isAuthenticated: boolean;
}

const ReceiptActionButtons: FC<ReceiptActionButtonsProps> = ({
  business,
  purchase,
  fetchData,
  isAuthenticated,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const [showCalendarExportModal, setShowCalendarExportModal] = useState<boolean>(false);

  const { patron, jwtToken } = useApp();
  const { actions } = useActions();
  const history = useHistory();
  const { url } = useRouteMatch();
  const appTarget = getAppTarget();
  const isPQCEnabled = useFeatureFlag('PAID_QUICK_CHECKOUT');
  const isPQCTokenizedLinkAlertEnabled = useFeatureFlag('TOKENIZED_LINK_ACCOUNT_ALERT');

  const handleCalendarClick = () => {
    amplitudeSendEvent(ACTION_CLICK_ADD_TO_CALENDAR, {
      businessId: business.id ?? undefined,
      businessName: business.name ?? undefined,
      reservationDateTime: purchase.ticketDateTime,
      ...experienceToData(purchase.ticketType || undefined),
    });

    if (appTarget === 'iOS') {
      window.iOS.addReservationToCalendar(purchase.id);
    } else {
      setShowCalendarExportModal(true);
    }
  };

  const handleModifyClick = () => {
    amplitudeSendEvent(ACTION_START_MODIFY_RESERVATION, { ...pastPurchaseToData(purchase) });

    const { token } = parse(location.search);
    actions.reschedule.setRescheduleInfo(purchase, token);
    history.push(`/${purchase?.business?.domainName}/reschedule/start`);
  };

  const handleTransferClick = () => {
    amplitudeSendEvent(ACTION_START_TRANSFER_RESERVATION, { ...pastPurchaseToData(purchase) });

    actions.transfer.setTransferInfo(purchase);
    history.push('/profile/start-transfer/option');
  };

  const handleCancelTransferClick = () => {
    amplitudeSendEvent(ACTION_START_CANCEL_TRANSFER_RESERVATION, {
      ...pastPurchaseToData(purchase),
    });

    actions.transfer.cancel(purchase.id).then(fetchData);
  };

  const handleCancelClick = () => {
    const { token } = parse(location.search);
    amplitudeSendEvent(ACTION_START_CANCEL_RESERVATION, { ...pastPurchaseToData(purchase) });
    history.push({
      pathname: joinPath(url, '/cancel'),
      search: stringify({ token }),
    });
  };

  /**
   * Currently, reservations with paid addons cannot be booked/modified without logging in. As such,
   * we check if the current purchase has paid addons, and the user isn't authenticated. If it's true,
   * we disable modify, since modifying in these cases result in an error, even pre-PQC.
   */
  const shouldOverridePQCDisable = () => {
    return (
      !isAuthenticated &&
      purchase.ticketType?.optionGroup?.some((optionGroup) =>
        optionGroup?.ticketOption?.some((option) => option?.defaultPrice ?? 0 > 0)
      )
    );
  };

  const modifiableUntil = Instant.ofEpochMilli(purchase.modifiableUntilTimestamp);
  const reservationIsInPast = modifiableUntil.isBefore(Instant.now());

  if (purchase.cancelledOrRefunded || reservationIsInPast) {
    return null;
  }

  const owner = purchase.ownerPatron ? (purchase.ownerPatron as core.Patron) : undefined;
  const showCalendar = !!owner && (appTarget === 'Web' || (isAuthenticated && appTarget === 'iOS'));

  const isGiftCardPurchase = some(purchase.discount, (discount) => !isNil(discount.giftCard));
  const showTransfer =
    isAuthenticated &&
    purchase.canTransfer &&
    !purchase.inTransfer &&
    !isGiftCardPurchase &&
    (hasAnyMoniesInvolved(purchase) || purchase.paymentCardHold);

  const showCancelTransfer = purchase.inTransfer;
  const { TicketType } = protos;
  const showModify =
    purchase.canReschedule && !purchase.cancelledOrRefunded && !business.bookingsDisabled;
  const showCancel = purchase.canCancel && !purchase.cancelledOrRefunded;
  const isTicketTypeReservation = safeEnumCompare(
    purchase.ticketTypeVariety,
    TicketType.Variety,
    TicketType.Variety.RESERVATION
  );
  const disableActionsForPQC =
    shouldUsePaidQuickCheckout(purchase, jwtToken, isPQCEnabled) || shouldOverridePQCDisable();
  const purchaseToken = new URLSearchParams(window.location.search).get('token');
  const displayTokenizedLinkLoggedInUserMessage =
    purchaseToken &&
    isAuthenticated &&
    isPQCTokenizedLinkAlertEnabled &&
    patron?.email !== purchase.ownerPatron?.email;

  return (
    <>
      {disableActionsForPQC && (showModify || showCancel) && (
        /**
         * Currently, users can cancel via e-mail without logging in if the ticketType === RESERVATION, so showCancel
         * is passed in this way to mimic that behavior - we'll only disable cancel if usePQC is true, and the
         * ticketType is NOT reservation - so disable for full, deposit, etc.
         */
        <PQCLoginForm showModify={showModify} showCancel={showCancel && !isTicketTypeReservation} />
      )}
      {displayTokenizedLinkLoggedInUserMessage && (
        <Alert severity="info">
          <Typography mb={1}>
            To modify or cancel your reservation, please make sure you're using the same email
            associated with the booked reservation.
          </Typography>
        </Alert>
      )}
      <CollapsibleButtonGroup
        collapsedChildProps={{ variant: 'link' }}
        cutoff={{ xs: 1, sm: 2, md: 3 }}
      >
        {showModify && (
          <ReceiptModifyButton onClick={handleModifyClick} disabled={disableActionsForPQC} />
        )}
        {showCalendar && <ReceiptCalendarButton onClick={handleCalendarClick} />}
        {showTransfer && <ReceiptTransferButton onClick={handleTransferClick} />}
        {showCancelTransfer && <ReceiptCancelTransferButton onClick={handleCancelTransferClick} />}
        {showCancel && (
          <ReceiptCancelButton
            onClick={handleCancelClick}
            disabled={disableActionsForPQC && !isTicketTypeReservation}
          />
        )}
      </CollapsibleButtonGroup>
      {showCalendar && (
        <CalendarExportModal
          purchase={purchase}
          patron={owner}
          onClose={() => setShowCalendarExportModal(false)}
          isOpen={showCalendarExportModal}
        />
      )}
    </>
  );
};

export default ReceiptActionButtons;
